// ConfirmationModal.tsx
import { BookingCreateDTO, ServiceResponseDTO } from "./types/ZaprosDTO";
import styles from "./Modals.module.css";

// ==================== ТИПЫ ПРОПСОВ ====================
type Props = {
  bookingData: BookingCreateDTO;
  service?: ServiceResponseDTO;
  onConfirm: () => void;
  onCancel: () => void;
};

// ==================== КОМПОНЕНТ МОДАЛЬНОГО ОКНА ====================
export const ConfirmationModal = ({
  bookingData,
  service,
  onConfirm,
  onCancel
}: Props) => {
  // ==================== ВЫЧИСЛЯЕМЫЕ ЗНАЧЕНИЯ ====================
  const isPersonType = service?.person_based_pricing;
  const totalPrice = bookingData.total_price || 0;

  // ==================== ФОРМАТИРОВАНИЕ ВРЕМЕНИ (САМАРА UTC+4) ====================
  const formatTimeString = (time?: string) => {
    if (!time) return '--:--';
    
    try {
      // Разбиваем строку времени на компоненты
      const [hoursStr, minutesStr] = time.split(':');
      let hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      
      // Корректируем часы при переходе через полночь
      if (hours >= 24) hours -= 24;
      
      // Форматируем с ведущими нулями
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      console.error('Ошибка форматирования времени:', error);
      return '--:--';
    }
  };

  // ==================== ФОРМАТИРОВАНИЕ ДАТЫ ====================
  const formatDate = (dateString: string) => {
    try {
      // Преобразуем в локальный формат даты
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Ошибка форматирования даты:', error);
      return dateString;
    }
  };

  // ==================== ВАЛИДАЦИЯ ПЕРЕД ПОДТВЕРЖДЕНИЕМ ====================
  const handleConfirmWithCheck = () => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    
    const errors = [
      !service?.service_id && "Не выбрана услуга",
      !dateRegex.test(bookingData.booking_date) && "Неверный формат даты",
      !timeRegex.test(bookingData.start_time) && "Неверное время начала",
      !timeRegex.test(bookingData.end_time) && "Неверное время окончания",
      bookingData.quantity <= 0 && "Некорректное количество"
    ].filter(Boolean);

    if (errors.length > 0) {
      alert(`Ошибки валидации:\n${errors.join("\n")}`);
      return;
    }

    onConfirm();
  };

  // ==================== ОБРАБОТКА ОТМЕНЫ ====================
  const handleCancelWithLog = () => {
    console.log('Отмена бронирования:', { 
      bookingData, 
      service 
    });
    onCancel();
  };

  // ==================== РЕНДЕРИНГ КОМПОНЕНТА ====================
  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <h2>Подтверждение бронирования</h2>
        
        <div className={styles.details}>
          {/* Блок информации об услуге */}
          <p className={styles.detailItem}>
            <span>Услуга:</span> {service?.name || 'Не указана'}
          </p>
          
          {/* Блок даты */}
          <p className={styles.detailItem}>
            <span>Дата:</span> {formatDate(bookingData.booking_date)}
          </p>

          {/* Условный рендеринг для персонализированных услуг */}
          {isPersonType ? (
            <>
            <p className={styles.detailItem}>
              <span>Количество:</span> {bookingData.quantity}
            </p>
            <p className={styles.detailItem}>
              <span>Описание:</span> {service?.description || 'услуги'} 
            </p>
          </>
        ) : (
          <>
              <p className={styles.detailItem}>
                <span>Начало:</span> {formatTimeString(bookingData.start_time)}
              </p>
              <p className={styles.detailItem}>
                <span>Окончание:</span> {formatTimeString(bookingData.end_time)}
              </p>
            </>
          )}

          {/* Блок итоговой стоимости */}
          <div className={styles.totalPrice}>
            Итого: {totalPrice.toLocaleString('ru-RU')}₽
          </div>
        </div>

        {/* Блок кнопок управления */}
        <div className={styles.controls}>
          <button 
            className={styles.cancelButton}
            onClick={handleCancelWithLog}
          >
            Изменить
          </button>
          <button 
            className={styles.confirmButton}
            onClick={handleConfirmWithCheck}
          >
            Подтвердить
          </button>
        </div>
      </div>
    </div>
  );
};